// The rhythm of scheduled bot work (agents/schedules.py) and task recurrence
// in the viewer's language. Plural units come from Intl, so every locale
// gets its own grammar ("2 hours", "2 hodiny", "5 hodín").

// 2026-10-05 is a Monday: day names Monday first.
export const dayName = (d, lang, style = 'long') => new Intl.DateTimeFormat(lang, { weekday: style })
  .format(new Date(Date.UTC(2026, 9, 5 + Number(d), 12)));

const unit = (lang, value, name) => {
  try {
    return new Intl.NumberFormat(lang, { style: 'unit', unit: name, unitDisplay: 'long' }).format(value);
  } catch {
    return `${value} ${name}`;
  }
};

const dayList = (days, lang, style = 'long') => {
  const names = (days || []).map((d) => dayName(d, lang, style));
  try {
    return new Intl.ListFormat(lang, { style: 'long', type: 'conjunction' }).format(names);
  } catch {
    return names.join(', ');
  }
};

const daysOf = (c) => (c.days && c.days.length ? c.days : c.weekday != null ? [c.weekday] : []);

export const cadenceText = (t, c, lang) => {
  if (!c) return '';
  const time = c.time;
  switch (c.type) {
    case 'once': {
      let date = c.date;
      try { date = new Intl.DateTimeFormat(lang, { weekday: 'short', day: 'numeric', month: 'long' }).format(new Date(`${c.date}T12:00:00`)); } catch { /* keep ISO */ }
      return t('bots.cadence.text_once', { date, time });
    }
    case 'hourly': {
      const every = Number(c.every || 1);
      let text = every === 1 ? t('bots.cadence.text_hourly_one', { time })
        : t('bots.cadence.text_hourly', { amount: unit(lang, every, 'hour'), time });
      if (c.until) text += t('bots.cadence.text_until', { time: c.until });
      if (daysOf(c).length) text += t('bots.cadence.text_on_days', { days: dayList(daysOf(c), lang, 'short') });
      return text;
    }
    case 'daily': return t('bots.cadence.text_daily', { time });
    case 'weekdays': return t('bots.cadence.text_weekdays', { time });
    case 'weekly': return t('bots.cadence.text_weekly', { days: dayList(daysOf(c), lang), time });
    case 'monthly': return c.day === -1 ? t('bots.cadence.text_monthly_last', { time })
      : t('bots.cadence.text_monthly', { day: c.day || 1, time });
    default: return '';
  }
};

export const recurrenceText = (t, r, lang) => {
  if (!r) return '';
  const n = Number(r.interval || 1);
  let text;
  switch (r.type) {
    case 'daily': text = n === 1 ? t('bots.repeat.daily') : t('bots.repeat.every', { amount: unit(lang, n, 'day') }); break;
    case 'weekdays': text = t('bots.repeat.weekdays'); break;
    case 'weekly': text = n === 1 ? t('bots.repeat.weekly') : t('bots.repeat.every', { amount: unit(lang, n, 'week') }); break;
    case 'biweekly': text = t('bots.repeat.every', { amount: unit(lang, 2, 'week') }); break;
    case 'monthly': text = n === 1 ? t('bots.repeat.monthly') : t('bots.repeat.every', { amount: unit(lang, n, 'month') }); break;
    case 'custom': text = t('bots.repeat.on_days', { days: dayList(r.days_of_week, lang) }); break;
    default: text = '';
  }
  if (r.end_date) text += t('bots.repeat.until', { date: formatDay(r.end_date, lang) });
  return text;
};

/** A run time: "Mon 5 Oct, 09:00" in the viewer's language (and time zone). */
export const formatRun = (iso, lang, timeZone) => {
  if (!iso) return '';
  try {
    return new Intl.DateTimeFormat(lang, {
      weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
      ...(timeZone ? { timeZone } : {}),
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

export const formatDay = (iso, lang) => {
  if (!iso) return '';
  const d = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(`${iso}T12:00:00`) : new Date(iso);
  try {
    return new Intl.DateTimeFormat(lang, { weekday: 'short', day: 'numeric', month: 'short' }).format(d);
  } catch {
    return iso;
  }
};

export const relative = (iso, lang) => {
  if (!iso) return '';
  const minutes = Math.round((new Date(iso).getTime() - Date.now()) / 60000);
  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });
  if (Math.abs(minutes) < 60) return rtf.format(minutes, 'minute');
  if (Math.abs(minutes) < 60 * 24) return rtf.format(Math.round(minutes / 60), 'hour');
  return rtf.format(Math.round(minutes / 1440), 'day');
};

export const CADENCE_TYPES = ['once', 'hourly', 'daily', 'weekdays', 'weekly', 'monthly'];

/** A sensible cadence of a type, keeping the time already picked. */
export const cadenceOfType = (type, prev = {}) => {
  const time = prev.time || '08:00';
  const tomorrow = new Date(Date.now() + 86400000);
  const date = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, '0')}-${String(tomorrow.getDate()).padStart(2, '0')}`;
  switch (type) {
    case 'once': return { type, time, date: prev.date || date };
    case 'hourly': return { type, time, every: prev.every || 2, until: prev.until || '18:00', days: prev.days || [0, 1, 2, 3, 4] };
    case 'weekly': return { type, time, days: daysOf(prev).length ? daysOf(prev) : [0] };
    case 'monthly': return { type, time, day: prev.day || 1 };
    default: return { type, time };
  }
};

export const browserTimeZone = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return 'UTC'; }
};
